<script>
import { mapGetters } from 'vuex'
import HeaderImage from '../components/HeaderImage.vue'
import PackageDealResource from '@/resources/packageDeal'
import PackageDealGroupResource from '@/resources/packageDealGroup'
import messages from '@/mixins/messages'

export default {
    name: 'Deals',
    mixins: [messages],
    components: { HeaderImage },
    computed: {
        ...mapGetters(['currencySymbol']),
        embedMode() {
            return this.$route.query.nav === 'false'
        },
        singleDealId() {
            return this.$route.query.deal || null
        },
        singleGroupId() {
            return this.$route.query.group || null
        },
        isSingle() {
            return !!(this.singleDealId || this.singleGroupId)
        },
        groupedDealIds() {
            const ids = new Set()
            for (const group of this.groupResources) {
                for (const item of this.itemsOf(group)) ids.add(item.deal.id)
            }
            return ids
        },
        standaloneDeals() {
            return this.dealResources.filter((deal) => !this.groupedDealIds.has(deal.id))
        }
    },
    data() {
        return {
            loading: true,
            dealResources: [],
            groupResources: [],
            singleDeal: null,
        }
    },
    methods: {
        itemsOf(group) {
            return (group.attributes && group.attributes.items) || []
        },
        attrsOf(deal) {
            return deal.attributes ? deal.attributes : deal
        },
        idOf(deal) {
            return deal.id || this.attrsOf(deal).id
        },
        heroUrl(deal) {
            const rv = this.attrsOf(deal).readonly_values
            return (rv && rv.hero_image_url) || null
        },
        heroStyle(deal) {
            const url = this.heroUrl(deal)
            return url ? { backgroundImage: `url(${url})` } : {}
        },
        priceLabel(deal) {
            const a = this.attrsOf(deal)
            const symbol = (a.readonly_values && a.readonly_values.currency_symbol) || this.currencySymbol
            if (a.pricing_mode === 'FLAT') {
                return `${symbol}${this.moneyFormat(a.price_cents)}`
            }
            if (a.adjustment_type === 'PERCENTAGE') {
                return `${a.value}% ${a.direction === 'SURCHARGE' ? 'more' : 'off'}`
            }
            return `${symbol}${this.moneyFormat(a.value)} ${a.direction === 'SURCHARGE' ? 'more' : 'off'}`
        },
        choose(deal) {
            const id = this.idOf(deal)
            if (this.embedMode) {
                const { href } = this.$router.resolve({ name: 'reservations', query: { deal: id } })
                window.top.location.href = `${window.location.origin}${href}`
                return
            }
            this.$router.push({ name: 'reservations', query: { deal: id } })
        }
    },
    async mounted() {
        try {
            if (this.singleDealId) {
                this.singleDeal = await PackageDealResource.detail(this.singleDealId)
            } else if (this.singleGroupId) {
                this.groupResources = [await PackageDealGroupResource.detail(this.singleGroupId)]
            } else {
                const [deals, groups] = await Promise.all([
                    PackageDealResource.filterAll({ active: true }),
                    PackageDealGroupResource.filterAll({ active: true }),
                ])
                this.dealResources = deals
                this.groupResources = groups
            }
        } catch (e) {
            this.addMessage('warning', 'Deals could not be loaded right now.')
        } finally {
            this.loading = false
        }
    },
}
</script>

<template>
    <div :class="['deals-page', { 'deals-page--embed': embedMode }]">
        <header-image src="hero-main5.jpg" v-if="!isSingle">
            <h1>Angebote</h1>
        </header-image>

        <b-container :class="isSingle ? 'py-4' : 'mt-4 mb-5'">
            <div v-if="messages.length">
                <b-alert :variant="message.variant" show v-for="(m, i) in messages" :key="i">{{ m.text }}</b-alert>
            </div>

            <div v-if="loading" class="text-center py-5"><b-spinner label="Wird geladen" /></div>

            <template v-else>
                <!-- Single deal (embeddable) -->
                <article v-if="singleDeal" class="deal-card deal-card--solo mx-auto">
                    <div class="deal-card__media" v-if="heroUrl(singleDeal)" :style="heroStyle(singleDeal)">
                        <p class="deal-card__aux" v-if="attrsOf(singleDeal).aux_cell">{{ attrsOf(singleDeal).aux_cell }}</p>
                    </div>
                    <div class="deal-card__body">
                        <h1 class="deal-card__name">{{ attrsOf(singleDeal).name }}</h1>
                        <p class="deal-card__price">{{ priceLabel(singleDeal) }}<span class="deal-card__unit" v-if="attrsOf(singleDeal).price_unit_label"> {{ attrsOf(singleDeal).price_unit_label }}</span></p>
                        <div class="deal-card__desc" v-html="attrsOf(singleDeal).description"></div>
                        <p class="deal-card__aux deal-card__aux--plain" v-if="attrsOf(singleDeal).aux_cell && !heroUrl(singleDeal)">{{ attrsOf(singleDeal).aux_cell }}</p>
                        <b-button variant="primary" size="lg" block class="mt-auto" @click="choose(singleDeal)">Auswählen</b-button>
                    </div>
                </article>

                <template v-else>
                    <b-alert variant="info" show v-if="!groupResources.length && !standaloneDeals.length">
                        Zurzeit sind keine Angebote verfügbar. Schau bald wieder vorbei!
                    </b-alert>

                    <!-- Presentation groups -->
                    <section v-for="group in groupResources" :key="group.id" class="deal-group">
                        <header class="deal-group__head" v-if="!embedMode || groupResources.length > 1 || group.attributes.description">
                            <h2 class="deal-group__title">{{ group.attributes.name }}</h2>
                            <div class="deal-group__desc" v-if="group.attributes.description" v-html="group.attributes.description"></div>
                        </header>

                        <div class="deal-tiers">
                            <article
                                v-for="item in itemsOf(group)"
                                :key="item.id"
                                :class="['deal-card', { 'deal-card--featured': item.is_highlighted }]">
                                <div class="deal-card__ribbon bg-primary text-white" v-if="item.is_highlighted">{{ item.highlight_label || 'Am beliebtesten' }}</div>
                                <p class="deal-card__tierlabel" v-if="item.tier_label">{{ item.tier_label }}</p>
                                <div class="deal-card__media" v-if="heroUrl(item.deal)" :style="heroStyle(item.deal)">
                                    <p class="deal-card__aux" v-if="item.deal.aux_cell">{{ item.deal.aux_cell }}</p>
                                </div>
                                <div class="deal-card__body">
                                    <h3 class="deal-card__name">{{ item.deal.name }}</h3>
                                    <p class="deal-card__price">{{ priceLabel(item.deal) }}<span class="deal-card__unit" v-if="item.deal.price_unit_label"> {{ item.deal.price_unit_label }}</span></p>
                                    <div class="deal-card__desc" v-html="item.deal.description"></div>
                                    <p class="deal-card__aux deal-card__aux--plain" v-if="item.deal.aux_cell && !heroUrl(item.deal)">{{ item.deal.aux_cell }}</p>
                                    <b-button
                                        block
                                        class="mt-auto"
                                        variant="primary"
                                        @click="choose(item.deal)">Auswählen</b-button>
                                </div>
                            </article>
                        </div>
                    </section>

                    <!-- Standalone deals -->
                    <section v-if="standaloneDeals.length" class="deal-group">
                        <header class="deal-group__head" v-if="groupResources.length">
                            <h2 class="deal-group__title">Weitere Angebote</h2>
                        </header>
                        <div class="deal-tiers">
                            <article v-for="deal in standaloneDeals" :key="deal.id" class="deal-card">
                                <div class="deal-card__media" v-if="heroUrl(deal)" :style="heroStyle(deal)">
                                    <p class="deal-card__aux" v-if="attrsOf(deal).aux_cell">{{ attrsOf(deal).aux_cell }}</p>
                                </div>
                                <div class="deal-card__body">
                                    <h3 class="deal-card__name">{{ attrsOf(deal).name }}</h3>
                                    <p class="deal-card__price">{{ priceLabel(deal) }}<span class="deal-card__unit" v-if="attrsOf(deal).price_unit_label"> {{ attrsOf(deal).price_unit_label }}</span></p>
                                    <div class="deal-card__desc" v-html="attrsOf(deal).description"></div>
                                    <p class="deal-card__aux deal-card__aux--plain" v-if="attrsOf(deal).aux_cell && !heroUrl(deal)">{{ attrsOf(deal).aux_cell }}</p>
                                    <b-button block class="mt-auto" variant="primary" @click="choose(deal)">Auswählen</b-button>
                                </div>
                            </article>
                        </div>
                    </section>
                </template>
            </template>
        </b-container>
    </div>
</template>

<style lang="scss">
.deals-page {
    &--embed {
        background: transparent;
    }
}

.deal-group {
    margin-bottom: 3.5rem;

    &__head {
        text-align: center;
        max-width: 42rem;
        margin: 0 auto 2rem;
    }

    &__title {
        font-weight: 800;
        font-style: italic;
        text-transform: uppercase;
        letter-spacing: 0.02em;
    }

    &__desc {
        color: var(--muted);
    }
}

.deal-tiers {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: stretch;
    gap: 1.5rem;
}

.deal-card {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 20rem;
    max-width: 100%;
    background: var(--panel);
    border: 1px solid var(--border);
    border-radius: 0.9rem;
    color: var(--ink);
    overflow: hidden;
    box-shadow: 0 6px 22px rgba(0, 0, 0, 0.07);
    transition: transform 0.15s ease, box-shadow 0.15s ease;

    &--featured {
        border-color: transparent;
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
        outline: 2px solid var(--sos-accent, #ff2200);

        @media (min-width: 768px) {
            transform: scale(1.045);
            z-index: 2;
        }
    }

    &--solo {
        width: 34rem;
    }

    &__ribbon {
        position: absolute;
        top: 0.9rem;
        right: -2.6rem;
        transform: rotate(35deg);
        padding: 0.28rem 3rem;
        font-size: 0.72rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        z-index: 3;
    }

    &__tierlabel {
        margin: 0;
        padding: 0.6rem 1.25rem 0;
        font-size: 0.78rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: var(--muted);
    }

    &__media {
        position: relative;
        height: 11rem;
        background-size: cover;
        background-position: center;
    }

    &__aux {
        position: absolute;
        top: 0.75rem;
        left: 0.75rem;
        max-width: calc(100% - 1.5rem);
        margin: 0;
        padding: 0.25rem 0.85rem;
        border-radius: 999px;
        background: rgba(0, 0, 0, 0.45);
        -webkit-backdrop-filter: blur(3px);
        backdrop-filter: blur(3px);
        color: #fff;
        font-size: 0.78rem;
        font-weight: 600;
        line-height: 1.45;

        &--plain {
            position: static;
            display: inline-block;
            max-width: 100%;
            background: var(--panel-2);
            color: var(--ink);
            margin-bottom: 1rem;
        }
    }

    &__body {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        padding: 1.5rem 1.35rem 1.5rem;
        text-align: center;
    }

    &__name {
        font-weight: 700;
        margin-bottom: 0.35rem;
        color: var(--text-heading);
    }

    &__price {
        font-size: 2rem;
        font-weight: 800;
        line-height: 1;
        margin-bottom: 1rem;
        color: var(--ink);
    }

    &__unit {
        font-size: 0.9rem;
        font-weight: 500;
        color: var(--muted);
    }

    &__desc {
        color: var(--muted);
        font-size: 0.95rem;
        margin-bottom: 1.25rem;

        * {
            color: inherit !important;
            background-color: transparent !important;
        }
    }
}
</style>
